import AccountApi from 'apis/accountApi'
import history from 'utils/browserHistory'
import Token from 'utils/token'
import { parseJwt, setCurrentUser } from 'utils/user'
import i18n from 'i18n/index'
import ACTION_TYPES from './actionTypes'
import type {
  StartLoadingAction,
  LoginFailedAction,
  LoginSuccessAction,
  LoginActionFunc,
  LoginSuccessActionFunc
} from './types'
import { typesUrlsCache } from 'apis/listCache'
import { TokenData, User } from 'constants/types'
import { KEY_STORAGE_LAST_PATH, PATH_TERMINAL } from 'routes/paths'

const startLoading = (): StartLoadingAction => ({
  type: ACTION_TYPES.START_LOADING,
  error: ''
})
const loginSuccess = (): LoginSuccessAction => ({
  type: ACTION_TYPES.LOGIN_SUCCESS,
  error: ''
})
const loginFailed = (error: string): LoginFailedAction => ({
  type: ACTION_TYPES.LOGIN_FAILED,
  error
})

const removeCache = async () => {
  const cacheName = 'hub-cache'
  const cachePromise = caches.open(cacheName)
  const cache = await cachePromise
  const cacheKeys = await cache.keys()
  // Filtrar las entradas de la caché que tienen más de una hora de antigüedad
  const keysToDelete = cacheKeys.filter(async (cacheKey) => {
    const cacheResponse = await cache.match(cacheKey)
    if (!cacheResponse || !cacheResponse.ok) {
      return null
    }

    const response = await cacheResponse.json()
    return Boolean((typesUrlsCache)[response?.typeRequest])
  })
  // Eliminar las entradas de la caché que tienen más de una hora de antigüedad
  await Promise.all(keysToDelete.map((key) => cache.delete(key)))
}

type cahcheStorage = {
  token?: Token
  time?: number,
}

export const updateCache = (token: Token) => {
  let lastUserCache: cahcheStorage | string | null = localStorage.getItem('lastUserCache')
  const getCurrentDay = new Date().getDate()
  if (!lastUserCache) {
    localStorage.setItem('lastUserCache', JSON.stringify({ token, time: getCurrentDay }))
    removeCache().then(() => {})
    return
  }

  if (typeof lastUserCache === 'string') {
    lastUserCache = JSON.parse(lastUserCache as string) as cahcheStorage
  }

  if (lastUserCache?.token) {
    const oldToken: TokenData = parseJwt(lastUserCache?.token)
    const newToken: TokenData = parseJwt(token)
    if (oldToken && oldToken?.email === newToken?.email && getCurrentDay === lastUserCache?.time) {
      return
    }
  }

  localStorage.setItem('lastUserCache', JSON.stringify({ token, time: getCurrentDay }))
  removeCache().then(() => { })
  return
}

export const login: LoginActionFunc = (credentials) => async (dispatch) => {
  dispatch(startLoading())
  return AccountApi.obtainToken(credentials)
    .then((res) => {
      const token = new Token().setToken(res.access_token, res.refresh_token)

      const exp = Number(res.expires_in)
      const expRefresh = Number(res.refresh_expires_in)

      const obj: User = {
        token: res.access_token,
        accessToken: res.access_token,
        refreshToken: res.refresh_token,
        tickerVisible: false,
        iat: 0,
        exp,
        expRefresh,
        userId: '',
        tokenData: null,
        diferenceTime: 0,
        group: ''
      }
      setCurrentUser(obj, token)
      localStorage.removeItem('errorLogin')
      updateCache(token || '')
      dispatch(loginSuccess())
    })
    .then(() => {
      history.go(localStorage.getItem(KEY_STORAGE_LAST_PATH) || PATH_TERMINAL, { state: { fromDashboard: true } })
    })
    .catch((err) => {
      if (err && err.status === 401 && err.reason === 'AuthenticationError') dispatch(loginFailed(i18n.t('loginError')))
      localStorage.setItem('errorLogin', 'true')
      return Promise.resolve(err)
    })
}

export const clearBackendValidation: LoginSuccessActionFunc = () => (dispatch) => {
  dispatch(loginSuccess())
}

export const updateConnectionStatus = (connectionStatus: string) => ({
  type: ACTION_TYPES.UPDATE_CONNECTION_STATUS,
  connectionStatus
})
